// frontend/src/components/Navigation.js

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  alpha,
  useTheme
} from '@mui/material';
import {
  AdminPanelSettings,
  AccountCircle,
  Business,
  PushPin,
  PushPinOutlined,
  Palette
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSidebarPrefs, SIDEBAR_WIDTH } from '../contexts/SidebarPrefsContext';
import { getVisibleSections } from '../config/menuSections';

const NAVBAR_HEIGHT = 64;

// variant:
//   'temporary' - overlay drawer opened from the navbar hamburger (default)
//   'docked'    - rendered in normal flow inside a page (landing dashboard)
//   'pinned'    - in the page flow under the navbar on every page, with the
//                 page content beside it (user preference)
const Navigation = ({ open, onClose, variant = 'temporary' }) => {
  const isTemporary = variant === 'temporary';
  const isPinned = variant === 'pinned';
  const { user, hasPageAccess } = useAuth();
  const { pinned, colors, setPinned, setColors, isDesktop } = useSidebarPrefs();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const isAdmin = user?.is_staff || user?.role === 'admin';

  // Sections filtered to what this user may see (shared with HomeDashboard)
  const visibleSections = getVisibleSections({ isAdmin, hasPageAccess });

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const handleTogglePin = () => {
    const next = !pinned;
    setPinned(next);
    // Pinning from the overlay: close it, the pinned copy takes over
    if (next && onClose) onClose();
  };

  // Section accent, or the neutral default when colours are switched off
  const accentFor = (section) =>
    colors ? section.color || theme.palette.primary.main : null;

  const paperSx = {
    width: SIDEBAR_WIDTH,
    background: 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)',
    borderRight: '1px solid rgba(0,0,0,0.08)',
    ...(variant === 'docked' && { position: 'relative', height: '100%', borderRadius: 3 }),
    // Pinned mode lives in the page flow under the sticky navbar: it scrolls
    // with the page and never gets its own scrollbar
    ...(isPinned && {
      position: 'relative',
      height: 'auto',
      minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      overflow: 'visible',
      borderTop: 'none'
    })
  };

  return (
    <Drawer
      anchor="left"
      variant={isTemporary ? 'temporary' : 'permanent'}
      open={isTemporary ? open : true}
      onClose={onClose}
      sx={!isTemporary ? { width: SIDEBAR_WIDTH, flexShrink: 0, height: isPinned ? 'auto' : '100%' } : undefined}
      PaperProps={{ sx: paperSx }}
    >
      {/* Preference controls: pin + colour toggle */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          bgcolor: alpha(theme.palette.primary.main, 0.04)
        }}
      >
        <Tooltip title={colors ? 'Colour-coded sections are on' : 'Colour-coded sections are off'}>
          <FormControlLabel
            sx={{ ml: 0, mr: 0 }}
            control={
              <Switch
                size="small"
                checked={colors}
                onChange={(e) => setColors(e.target.checked)}
                inputProps={{ 'aria-label': 'Toggle colour-coded sections' }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Palette fontSize="small" color={colors ? 'primary' : 'disabled'} />
                <Typography variant="body2" fontWeight={600}>
                  Section colours
                </Typography>
              </Box>
            }
          />
        </Tooltip>

        <Tooltip
          title={
            !isDesktop
              ? 'Pinning is available on larger screens'
              : pinned
              ? 'Unpin sidebar (go back to pop-out menu)'
              : 'Pin sidebar open (page shows beside it)'
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={handleTogglePin}
              disabled={!isDesktop}
              color={pinned ? 'primary' : 'default'}
              aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
            >
              {pinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {isTemporary && (
        <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Welcome, {user?.username}!
          </Typography>
          <Chip
            label={isAdmin ? 'Administrator' : user?.role || 'User'}
            color={isAdmin ? 'primary' : 'secondary'}
            size="small"
            icon={isAdmin ? <AdminPanelSettings /> : <AccountCircle />}
          />
          {user?.department && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Department: {user.department}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ overflowY: isPinned ? 'visible' : 'auto', flex: 1 }}>
        {visibleSections.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No pages available. Contact your administrator.
            </Typography>
          </Box>
        ) : (
          visibleSections.map((section, sectionIndex) => {
            const accent = accentFor(section);
            return (
              <Box key={sectionIndex}>
                {section.title && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 2, pb: 1 }}>
                    {accent && (
                      <Box sx={{ width: 4, height: 14, borderRadius: 1, bgcolor: accent }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: accent || 'text.secondary',
                        fontWeight: accent ? 700 : 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        lineHeight: 1
                      }}
                    >
                      {section.title}
                    </Typography>
                  </Box>
                )}
                <List disablePadding>
                  {section.items.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5, px: 2 }}>
                      <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        selected={location.pathname === item.path}
                        sx={
                          accent
                            ? {
                                borderRadius: 2,
                                borderLeft: `3px solid ${alpha(accent, 0.5)}`,
                                '& .MuiListItemIcon-root': { color: accent },
                                '&:hover': { backgroundColor: alpha(accent, 0.08) },
                                '&.Mui-selected': {
                                  backgroundColor: accent,
                                  borderLeftColor: accent,
                                  color: 'white',
                                  '& .MuiListItemIcon-root': { color: 'white' },
                                  '&:hover': { backgroundColor: accent, filter: 'brightness(0.9)' }
                                }
                              }
                            : {
                                borderRadius: 2,
                                '&.Mui-selected': {
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '& .MuiListItemIcon-root': { color: 'white' },
                                  '&:hover': { backgroundColor: 'primary.dark' }
                                }
                              }
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                {sectionIndex < visibleSections.length - 1 && (
                  <Divider sx={{ my: 1, mx: 2 }} />
                )}
              </Box>
            );
          })
        )}
      </Box>

      {isAdmin && (
        <>
          <Divider sx={{ mx: 2 }} />
          <Box sx={{ px: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Quick Actions
            </Typography>
            <ListItemButton
              onClick={() => window.open('/admin/', '_blank')}
              sx={{ borderRadius: 2, mb: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Business />
              </ListItemIcon>
              <ListItemText primary="Django Admin" />
            </ListItemButton>
          </Box>
        </>
      )}

      {/* Ownership notice - the app has no page footer, and the sidebar is on every page */}
      <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)', mt: 'auto' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          &copy; {new Date().getFullYear()} Gopak Ltd. All rights reserved.
        </Typography>
      </Box>
    </Drawer>
  );
};

export default Navigation;
